import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { LockIcon, ArrowRightIcon } from 'lucide-react';
import { LanguageProvider } from '../context/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { BlueprintHero } from '../components/blueprint/BlueprintHero';
import { BlueprintNav } from '../components/blueprint/BlueprintNav';
import { OverviewSection, GuestJourneySection, FivePortalsSection, StoryArcSection, FloorMapSection, FinalRevealSection } from '../components/blueprint/BlueprintExperience';
import { FoodDrinkSection, MusicSoundSection, AromaScentSection, ProductionSection } from '../components/blueprint/BlueprintSensory';
import { SponsorsSection, OntarioTourismSection, GurdwarasSection, PreviousSponsorsSection, SchoolsSection, WeddingPlannersSection, ResortSalesSection, InvestorsSection } from '../components/blueprint/BlueprintPartners';
import { DownloadsSection, FinalCTASection } from '../components/blueprint/BlueprintResources';
const CORRECT_PASSWORD = 'secretlocation';
const STORAGE_KEY = 'blueprint_access';
function PasswordGate({
  onSuccess
}: {
  onSuccess: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [shaking, setShaking] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      sessionStorage.setItem(STORAGE_KEY, 'granted');
      onSuccess();
    } else {
      setError(true);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    }
  };
  return <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-6" data-id="element-1292">
      {/* Background effects */}
      <div className="absolute inset-0" data-id="element-1293">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#d4a017]/5 rounded-full blur-[200px]" data-id="element-1294"></div>
      </div>
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: 'linear-gradient(#d4a017 1px, transparent 1px), linear-gradient(90deg, #d4a017 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }} data-id="element-1295"></div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="relative z-10 w-full max-w-md" data-id="element-1296">
        <div className="text-center mb-10" data-id="element-1297">
          <div className="w-16 h-16 border border-[#d4a017]/20 bg-[#d4a017]/5 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1298">
            <LockIcon className="w-7 h-7 text-[#d4a017]/60" data-id="element-1299" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#f5f0e8] mb-3 tracking-tight" data-id="element-1300">
            Experience <span className="text-[#d4a017]" data-id="element-1301">Blueprint</span>
          </h1>
          <p className="text-[#d4a017]/50 text-[10px] tracking-[0.25em] uppercase font-medium mb-2" data-id="element-1302">
            Prototype Edition
          </p>
          <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-1303">
            This prototype page is restricted to partners and collaborators.
          </p>
        </div>

        <form onSubmit={handleSubmit} data-id="element-1304">
          <motion.div animate={shaking ? {
          x: [-8, 8, -6, 6, -3, 3, 0]
        } : {}} transition={{
          duration: 0.4
        }} data-id="element-1305">
            <div className="relative mb-4" data-id="element-1306">
              <input type="password" value={password} onChange={e => {
              setPassword(e.target.value);
              setError(false);
            }} placeholder="Enter access code" className="w-full bg-[#0f0f23]/80 border border-[#d4a017]/20 focus:border-[#d4a017]/50 text-[#f5f0e8] placeholder-[#f5f0e8]/20 px-5 py-4 text-sm tracking-wider outline-none transition-colors" autoFocus data-id="element-1307" />
            </div>
            {error && <motion.p initial={{
            opacity: 0,
            y: -5
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-red-400/80 text-xs tracking-wider mb-4 text-center" data-id="element-1308">
                Incorrect access code. Please try again.
              </motion.p>}
            <button type="submit" className="w-full bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] px-6 py-4 text-xs tracking-[0.2em] uppercase font-medium transition-colors duration-300 flex items-center justify-center gap-2" data-id="element-1309">
              Access Blueprint
              <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-1310" />
            </button>
          </motion.div>
        </form>

        <p className="text-center text-[#f5f0e8]/20 text-xs mt-8 font-light" data-id="element-1311">
          Contact us if you need access credentials.
        </p>
      </motion.div>
    </div>;
}
export function BlueprintPage() {
  const [authenticated, setAuthenticated] = useState(false);
  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === 'granted') {
      setAuthenticated(true);
    }
  }, []);
  useEffect(() => {
    document.body.classList.add('bg-[#0a0a0f]', 'text-white', 'overflow-x-hidden');
    return () => {
      document.body.classList.remove('bg-[#0a0a0f]', 'text-white', 'overflow-x-hidden');
    };
  }, []);
  if (!authenticated) {
    return <PasswordGate onSuccess={() => setAuthenticated(true)} data-id="element-1312" />;
  }
  return <LanguageProvider data-id="element-1313">
      <div className="relative bg-[#0a0a0f] min-h-screen" data-id="element-1314">
        <Navbar data-id="element-1315" />
        <BlueprintNav data-id="element-1316" />

        <main className="w-full xl:pl-56" data-id="element-1317">
          <BlueprintHero data-id="element-1318" />
          <OverviewSection data-id="element-1319" />
          <GuestJourneySection data-id="element-1320" />
          <FivePortalsSection data-id="element-1321" />
          <StoryArcSection data-id="element-1322" />
          <FloorMapSection data-id="element-1323" />
          <FinalRevealSection data-id="element-1324" />
          <FoodDrinkSection data-id="element-1325" />
          <MusicSoundSection data-id="element-1326" />
          <AromaScentSection data-id="element-1327" />
          <ProductionSection data-id="element-1328" />
          <SponsorsSection data-id="element-1329" />
          <OntarioTourismSection data-id="element-1330" />
          <GurdwarasSection data-id="element-1331" />
          <PreviousSponsorsSection data-id="element-1332" />
          <SchoolsSection data-id="element-1333" />
          <WeddingPlannersSection data-id="element-1334" />
          <ResortSalesSection data-id="element-1335" />
          <InvestorsSection data-id="element-1336" />
          <DownloadsSection data-id="element-1337" />
          <FinalCTASection data-id="element-1338" />
        </main>

        <div className="xl:pl-56" data-id="element-1339">
          <Footer data-id="element-1340" />
        </div>
      </div>
    </LanguageProvider>;
}